/**
 * Render JSON artifact bodies as HTML tables when possible; otherwise pretty JSON.
 * Prefer structured tables / nested document blocks over raw JSON dumps.
 */

import React from 'react';
import { artifactCellText, coerceArtifactTable } from './coerceArtifactTable';
import { ArtifactBlocksBody } from './rendering/ArtifactBlocksBody';
import { asArtifactBlocks } from './rendering/artifactPrimitiveStyles';

interface JsonArtifactBodyProps {
  data: unknown;
  limitations?: string[];
}

function unwrapMaybeJson(data: unknown): unknown {
  if (typeof data !== 'string') return data;
  const trimmed = data.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return data;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return data;
  }
}

export const JsonArtifactBody: React.FC<JsonArtifactBodyProps> = ({ data, limitations }) => {
  const unwrapped = unwrapMaybeJson(data);

  // Nested document blocks inside json.data
  if (
    unwrapped &&
    typeof unwrapped === 'object' &&
    !Array.isArray(unwrapped) &&
    Array.isArray((unwrapped as { blocks?: unknown }).blocks)
  ) {
    const blocks = asArtifactBlocks((unwrapped as { blocks: unknown }).blocks);
    if (blocks.length > 0) {
      return <ArtifactBlocksBody blocks={blocks} limitations={limitations} />;
    }
  }

  const table = coerceArtifactTable(unwrapped);

  return (
    <div className="flex min-h-0 flex-col gap-3 text-base text-[var(--color-surface-text)]">
      {limitations && limitations.length > 0 ? (
        <p className="shrink-0 rounded border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-surface-text-secondary)]">
          {limitations.join(' ')}
        </p>
      ) : null}
      {table && table.rows.length > 0 ? (
        <div className="max-h-[min(70vh,calc(90vh-10rem))] overflow-auto rounded border border-[var(--color-border)]">
          <table className="min-w-full text-left text-sm">
            <thead className="sticky top-0 z-[1] bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]">
              <tr>
                {table.columns.map((col) => (
                  <th key={col} className="px-3 py-2 font-medium whitespace-nowrap">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, i) => (
                <tr key={i} className="border-t border-[var(--color-border)]">
                  {table.columns.map((_, j) => (
                    <td key={j} className="max-w-xs px-3 py-2 align-top break-words">
                      {artifactCellText(row[j])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <pre className="max-h-[min(70vh,calc(90vh-10rem))] overflow-auto whitespace-pre-wrap text-sm">
          {typeof unwrapped === 'string'
            ? unwrapped
            : JSON.stringify(unwrapped, null, 2)}
        </pre>
      )}
    </div>
  );
};
